import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const PIE_COLORS = ['#00d4ff', '#00ff88', '#ffd700', '#ff6b35', '#a855f7', '#22d3ee', '#f472b6'];
const chartAxis = { fontSize: 10, fontFamily: 'Share Tech Mono', fill: 'var(--txt2)' };

// ── Avatar theming ────────────────────────────────────────────────────────────
const TIER_AVATAR = {
  Probation: { bg: 'rgba(79,195,247,0.15)', border: '#4fc3f7', color: '#4fc3f7' },
  Core:      { bg: 'rgba(0,255,136,0.15)',   border: '#00ff88', color: '#00ff88' },
  Veteran:   { bg: 'rgba(255,215,0,0.15)',   border: '#ffd700', color: '#ffd700' },
  Legend:    { bg: 'rgba(255,107,53,0.15)',  border: '#ff6b35', color: '#ff6b35' },
  Intern:    { bg: 'rgba(168,85,247,0.15)',  border: '#a855f7', color: '#a855f7' },
};
const ROLE_AVATAR = {
  cwg:         { bg: 'rgba(255,107,53,0.15)', border: '#ff6b35', color: '#ff6b35' },
  sme:         { bg: 'rgba(0,212,255,0.15)',  border: '#00d4ff', color: '#00d4ff' },
  stakeholder: { bg: 'rgba(168,85,247,0.15)', border: '#a855f7', color: '#a855f7' },
};

export function getAvatarStyle(user, size = 52) {
  const theme = ROLE_AVATAR[user?.role] || TIER_AVATAR[user?.tier] || TIER_AVATAR.Probation;
  return {
    width: size, height: size, borderRadius: '50%',
    background: theme.bg, border: `2px solid ${theme.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.3, fontWeight: 800, color: theme.color,
    fontFamily: 'Orbitron', flexShrink: 0,
    boxShadow: `0 0 12px ${theme.border}40`,
    overflow: 'hidden', position: 'relative',
  };
}

// Preset avatar pool — emoji organized by category
export const AVATAR_PRESETS = [
  { group: 'Tech & AI',    items: ['🤖','👾','🦾','🧠','💻','🔬','🛸','🌐','🔮','⚗️','🔭','🌌','🧬','⚡','💡'] },
  { group: 'Achievement',  items: ['🏆','🎯','🌟','💎','🔥','🚀','👑','🎖️','⚔️','🛡️','🦁','🐉','🦅','🦊','🌈'] },
  { group: 'Nature & Fun', items: ['🐺','🦋','🐬','🦜','🌊','🏔️','🌙','☄️','🎸','🎨','🧩','🎲','🃏','🎭','🌸'] },
];

// Unified Avatar component — handles photo / preset emoji / initials
export function Avatar({ user, size = 52, onClick, style: extraStyle }) {
  const base = getAvatarStyle(user, size);
  const combined = { ...base, ...extraStyle, cursor: onClick ? 'pointer' : base.cursor };

  if (user?.avatarData?.startsWith('data:')) {
    return (
      <div style={combined} onClick={onClick}>
        <img src={user.avatarData} alt={user.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </div>
    );
  }
  if (user?.avatarData) {
    // preset emoji
    return (
      <div style={{ ...combined, fontSize: size * 0.48, background: combined.background }} onClick={onClick}>
        {user.avatarData}
      </div>
    );
  }
  // initials fallback
  return (
    <div style={{ ...combined, fontSize: size * 0.3, fontFamily: 'Orbitron' }} onClick={onClick}>
      {user?.avatar || '?'}
    </div>
  );
}

// ── Avatar Picker Modal ───────────────────────────────────────────────────────
export function AvatarPicker({ user, onSave, onClose }) {
  const [tab, setTab] = useState('library');
  const [selected, setSelected] = useState(user?.avatarData || null);
  const [preview, setPreview] = useState(null); // base64 for upload
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 3 * 1024 * 1024) { alert('File too large — max 3 MB'); return; }

    const reader = new FileReader();
    reader.onload = (e) => {
      const raw = e.target.result;
      // GIFs: preserve animation — skip canvas, store raw
      if (file.type === 'image/gif') {
        setPreview(raw);
        setSelected(raw);
        return;
      }
      // Static images: resize + circular crop via canvas
      const img = new Image();
      img.onload = () => {
        const MAX = 220;
        const scale = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setPreview(dataUrl);
        setSelected(dataUrl);
      };
      img.src = raw;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await onSave(selected);
      if (updated) onClose();
    } finally {
      setSaving(false);
    }
  };

  const previewUser = { ...user, avatarData: selected };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-title">CHANGE AVATAR</div>

        {/* Live preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: '12px 16px', background: 'var(--bg4)', borderRadius: 6 }}>
          <Avatar user={previewUser} size={64} />
          <div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>{user.name}</div>
            <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', marginTop: 3 }}>
              <span style={{ color: (TIER_AVATAR[user.tier] || TIER_AVATAR.Probation).border }}>{user.tier}</span> · {user.role?.toUpperCase()}
            </div>
            {selected && (
              <button onClick={() => { setSelected(null); setPreview(null); }}
                style={{ marginTop: 6, fontSize: 10, fontFamily: 'Share Tech Mono', color: 'var(--j6)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                ✕ Remove custom avatar
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['library', 'upload'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '6px 18px', borderRadius: 3, border: `1px solid ${tab === t ? 'var(--j1)' : 'var(--brd2)'}`,
                background: tab === t ? 'rgba(0,212,255,0.08)' : 'transparent',
                color: tab === t ? 'var(--j1)' : 'var(--txt2)',
                fontFamily: 'Share Tech Mono', fontSize: 11, cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase' }}>
              {t === 'library' ? '🎨 Avatar Library' : '📷 Upload Photo'}
            </button>
          ))}
        </div>

        {tab === 'library' && (
          <div>
            {AVATAR_PRESETS.map(group => (
              <div key={group.group} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 8 }}>{group.group}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {group.items.map(emoji => (
                    <button key={emoji} onClick={() => setSelected(emoji)}
                      style={{ width: 44, height: 44, borderRadius: '50%', fontSize: 22, cursor: 'pointer',
                        border: `2px solid ${selected === emoji ? 'var(--j1)' : 'var(--brd2)'}`,
                        background: selected === emoji ? 'rgba(0,212,255,0.12)' : 'var(--bg4)',
                        boxShadow: selected === emoji ? '0 0 10px rgba(0,212,255,0.4)' : 'none',
                        transition: 'all 0.15s' }}>
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'upload' && (
          <div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              style={{ border: `2px dashed ${dragOver ? 'var(--j1)' : 'var(--brd2)'}`,
                borderRadius: 8, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? 'rgba(0,212,255,0.04)' : 'transparent', transition: 'all 0.2s' }}
              onClick={() => document.getElementById('avatar-file-input').click()}>
              {preview ? (
                <img src={preview} alt="preview"
                  style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--j1)', boxShadow: '0 0 16px rgba(0,212,255,0.3)' }} />
              ) : (
                <>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>📷</div>
                  <div style={{ fontFamily: 'Share Tech Mono', fontSize: 12, color: 'var(--txt2)' }}>Drop image here or click to browse</div>
                  <div style={{ fontFamily: 'Share Tech Mono', fontSize: 10, color: 'var(--txt3)', marginTop: 4 }}>JPG · PNG · <span style={{ color: 'var(--j4)' }}>GIF (animated!)</span> · max 3 MB</div>
                </>
              )}
              <input id="avatar-file-input" type="file" accept="image/*,.gif" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={onClose}>CANCEL</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'SAVING…' : '✓ SAVE AVATAR'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Badge computation ─────────────────────────────────────────────────────────
const BADGE_DEFS = [
  { id: 'igniter',    pts: 100,   label: 'Igniter',       icon: '🔥', color: '#ff6b35', desc: '100+ points earned' },
  { id: 'contributor',pts: 500,   label: 'Contributor',   icon: '⚡', color: '#ffd700', desc: '500+ points earned' },
  { id: 'achiever',   pts: 1000,  label: 'Achiever',      icon: '🏆', color: '#00ff88', desc: '1,000+ points earned' },
  { id: 'champion',   pts: 5000,  label: 'Champion',      icon: '🌟', color: '#00d4ff', desc: '5,000+ points earned' },
  { id: 'legend_pts', pts: 10000, label: 'Legend',        icon: '💎', color: '#a855f7', desc: '10,000+ points earned' },
];

function computeBadges(user, stats) {
  const pts = user?.totalPoints || 0;
  const badges = BADGE_DEFS.filter(b => pts >= b.pts);
  if (stats?.contributions >= 1)  badges.push({ id: 'innovator', label: 'Innovator',    icon: '💡', color: '#a855f7', desc: 'Submitted a contribution' });
  if (stats?.quests >= 1)         badges.push({ id: 'seeker',    label: 'Quest Seeker', icon: '🎯', color: '#22d3ee', desc: 'Claimed a quest' });
  if (stats?.quests >= 5)         badges.push({ id: 'hunter',    label: 'Quest Hunter', icon: '⚔️', color: '#ffd700', desc: '5+ quests claimed' });
  if (stats?.peerReviewsGiven >= 3) badges.push({ id: 'reviewer', label: 'Reviewer',   icon: '🔍', color: '#f472b6', desc: '3+ peer reviews given' });
  if (stats?.pods >= 1)           badges.push({ id: 'team',      label: 'Team Player',  icon: '👥', color: '#00ff88', desc: 'Member of a pod' });
  if (stats?.library >= 1)        badges.push({ id: 'author',    label: 'Author',       icon: '📚', color: '#ffd700', desc: 'Library contributor' });
  return badges;
}

function BadgeRack({ user, stats }) {
  const badges = computeBadges(user, stats);
  if (!badges.length) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 8 }}>BADGES & ACHIEVEMENTS</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {badges.map(b => (
          <div key={b.id} title={b.desc} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
            border: `1px solid ${b.color}40`, borderRadius: 20,
            background: `${b.color}10`, cursor: 'default',
          }}>
            <span style={{ fontSize: 13 }}>{b.icon}</span>
            <span style={{ fontSize: 10, fontFamily: 'Share Tech Mono', fontWeight: 700, color: b.color, letterSpacing: 1 }}>{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
const tooltipStyle = { background: 'var(--bg3)', border: '1px solid var(--brd2)', borderRadius: 4, fontSize: 11, fontFamily: 'Share Tech Mono' };

function Overlay({ children, onClose, width = 720 }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: width, width: '92%' }} onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  );
}

function StatRow({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 18 }}>
      {items.map(i => (
        <div key={i.l}><div style={{ fontFamily: 'Orbitron', fontSize: 20, fontWeight: 800, color: 'var(--j1)' }}>{i.v}</div><div className="stat-label">{i.l}</div></div>
      ))}
    </div>
  );
}

function ChartCard({ title, children, height = 180 }) {
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 10 }}>{title}</div>
      <ResponsiveContainer width="100%" height={height}>{children}</ResponsiveContainer>
    </div>
  );
}

// ── Send Message modal ───────────────────────────────────────────────────────
export function SendMessageModal({ targetType, targetId, targetLabel, onClose }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post('/notifications/send', { targetType, targetId, message });
      setResult(`Sent to ${data.sent} recipient${data.sent === 1 ? '' : 's'}.`);
      setMessage('');
      setTimeout(onClose, 1200);
    } catch (e) {
      setResult(e.response?.data?.error || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <Overlay onClose={onClose} width={440}>
      <div className="modal-title">SEND MESSAGE</div>
      <div style={{ fontSize: 12, color: 'var(--txt2)', marginBottom: 14 }}>To: <span style={{ color: 'var(--j1)' }}>{targetLabel}</span></div>
      <textarea className="input" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Your message — delivered as an in-app notification…" />
      {result && <div style={{ fontSize: 12, color: 'var(--j5)', fontFamily: 'Share Tech Mono', margin: '10px 0' }}>{result}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={onClose}>CANCEL</button>
        <button className="btn btn-primary" onClick={send} disabled={sending || !message.trim()}>{sending ? 'SENDING…' : 'SEND'}</button>
      </div>
    </Overlay>
  );
}

// ── Member detail (profile + charts) ─────────────────────────────────────────
export function MemberDetail({ id, onClose, onDeleted }) {
  const { can, user: me, setUserData } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pickingAvatar, setPickingAvatar] = useState(false);

  const isSelf = me?.id === id;

  useEffect(() => {
    api.get(`/users/${id}/profile`).then(r => {
      setData(r.data);
      setNameVal(r.data.user.name);
    }).catch(e => setError(e.response?.data?.error || 'Failed to load'));
  }, [id]);

  const saveName = async () => {
    if (!nameVal.trim() || nameVal.trim() === data.user.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const { data: updated } = await api.patch(`/users/${id}/avatar`, { name: nameVal.trim() });
      setData(d => ({ ...d, user: updated }));
      if (isSelf) setUserData(updated);
    } catch {}
    setSavingName(false);
    setEditingName(false);
  };

  const saveAvatar = async (avatarData) => {
    try {
      const { data: updated } = await api.patch(`/users/${id}/avatar`, { avatarData: avatarData || null });
      setData(d => ({ ...d, user: updated }));
      if (isSelf) setUserData(updated);
      setPickingAvatar(false);
      return updated;
    } catch { return null; }
  };

  if (error) return <Overlay onClose={onClose} width={420}><div className="modal-title">MEMBER</div><div className="empty-state">{error}</div></Overlay>;
  if (!data) return <Overlay onClose={onClose} width={420}><div className="empty-state">Loading profile…</div></Overlay>;

  const { user, stats, charts, attachments } = data;
  const tierColors = { Probation: '#4fc3f7', Core: '#00ff88', Veteran: '#ffd700', Legend: '#ff6b35', Intern: '#a855f7' };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      onDeleted?.(user.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setConfirmDelete(false);
    }
    setDeleting(false);
  };

  return (
    <Overlay onClose={onClose}>
      {confirmDelete && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(2,4,8,0.92)', borderRadius: 8, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ fontFamily: 'Orbitron', fontSize: 18, color: 'var(--j6)', marginBottom: 12 }}>DELETE MEMBER</div>
          <div style={{ fontSize: 14, color: 'var(--txt2)', lineHeight: 1.7, textAlign: 'center', marginBottom: 8 }}>
            Permanently remove <strong style={{ color: 'var(--txt)' }}>{user.name}</strong>?
          </div>
          <div style={{ fontSize: 12, color: 'var(--j6)', background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.2)', borderRadius: 4, padding: '8px 14px', marginBottom: 24, textAlign: 'center' }}>
            ⚠ Their login will be deleted. Contributions &amp; history remain.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>CANCEL</button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting} style={{ minWidth: 140 }}>
              {deleting ? 'DELETING…' : '🗑 CONFIRM DELETE'}
            </button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar user={user} size={64} />
          {(isSelf || can('cwg')) && (
            <button onClick={() => setPickingAvatar(true)} title="Change avatar"
              style={{ position: 'absolute', bottom: 0, right: 0, width: 22, height: 22, borderRadius: '50%',
                background: 'var(--bg3)', border: '1px solid var(--j1)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--j1)' }}>
              📷
            </button>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <input className="input" style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 700, padding: '4px 8px' }}
                value={nameVal} onChange={e => setNameVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveName()} autoFocus />
              <button className="btn btn-sm btn-primary" onClick={saveName} disabled={savingName}>{savingName ? '…' : '✓'}</button>
              <button className="btn btn-sm" onClick={() => setEditingName(false)}>✕</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 700, color: 'var(--txt)' }}>{user.name}</div>
              {(isSelf || can('cwg')) && (
                <button onClick={() => setEditingName(true)} title="Edit name"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt3)', fontSize: 13, padding: 0, lineHeight: 1 }}>✎</button>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>
            {user.email} · <span style={{ color: tierColors[user.tier] }}>{user.tier}</span> · {user.role?.toUpperCase()}
            {user.committeeRole && <span style={{ color: 'var(--j4)', marginLeft: 6 }}>· {user.committeeRole}</span>}
          </div>
          {user.ssoOnly && <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 2 }}>SSO ONLY</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {can('cwg', 'stakeholder', 'sme') && !isSelf && <button className="btn btn-sm btn-primary" onClick={() => setMsg(true)}>💬 MSG</button>}
          {can('cwg') && user.role !== 'cwg' && (
            <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(true)} style={{ fontSize: 12 }}>🗑</button>
          )}
        </div>
      </div>

      <BadgeRack user={user} stats={stats} />

      <StatRow items={[
        { l: 'Total Points', v: (user.totalPoints || 0).toLocaleString() },
        { l: 'Contributions', v: stats.contributions },
        { l: 'Pods', v: stats.pods },
        { l: 'Quests', v: stats.quests },
        { l: 'Peer Reviews', v: stats.peerReviewsGiven },
      ]} />

      {charts.byCategory.length > 0 && (
        <ChartCard title="CONTRIBUTION POINTS BY CATEGORY">
          <BarChart data={charts.byCategory} layout="vertical" margin={{ left: 10, right: 10 }}>
            <XAxis type="number" tick={chartAxis} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="category" tick={chartAxis} axisLine={false} tickLine={false} width={140} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,212,255,0.05)' }} />
            <Bar dataKey="points" fill="#00d4ff" radius={[0, 3, 3, 0]} />
          </BarChart>
        </ChartCard>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {charts.timeline.length > 0 && (
          <ChartCard title="CUMULATIVE POINTS">
            <LineChart data={charts.timeline}>
              <XAxis dataKey="date" tick={chartAxis} axisLine={false} tickLine={false} />
              <YAxis tick={chartAxis} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="points" stroke="#00ff88" strokeWidth={2} dot={{ r: 3, fill: '#00ff88' }} />
            </LineChart>
          </ChartCard>
        )}
        {charts.byStatus.length > 0 && (
          <ChartCard title="CONTRIBUTIONS BY STATUS">
            <PieChart>
              <Pie data={charts.byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={60} label>
                {charts.byStatus.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'Share Tech Mono' }} />
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ChartCard>
        )}
      </div>

      {/* Attachments — where they are attached */}
      <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 1, margin: '8px 0 10px' }}>ATTACHMENTS</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <AttachList title="Pods" items={[...attachments.pods.map(p => `${p.name} · ${p.role}`), ...attachments.internPods.map(p => `${p.name} · ${p.role}`)]} />
        <AttachList title="Quests claimed" items={attachments.questsClaimed.map(q => `${q.title} · ${q.status}`)} />
        <AttachList title="Tasks" items={attachments.tasks.map(t => `${t.title} · ${t.progress}%`)} />
        <AttachList title="Library entries" items={attachments.libraryEntries.map(e => `${e.title} · ${e.reuses} reuses`)} />
      </div>

      {msg && <SendMessageModal targetType="member" targetId={user.id} targetLabel={user.name} onClose={() => setMsg(false)} />}
      {pickingAvatar && <AvatarPicker user={user} onSave={saveAvatar} onClose={() => setPickingAvatar(false)} />}
    </Overlay>
  );
}

function AttachList({ title, items }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--j1)', marginBottom: 8 }}>{title} <span style={{ color: 'var(--txt3)' }}>({items.length})</span></div>
      {items.length === 0 ? <div style={{ fontSize: 11, color: 'var(--txt3)' }}>None</div> :
        items.map((t, i) => <div key={i} style={{ fontSize: 11, color: 'var(--txt2)', padding: '3px 0', borderBottom: '1px solid var(--brd2)' }}>{t}</div>)}
    </div>
  );
}

// ── Pod detail (normal or intern) ────────────────────────────────────────────
// `pod` is the pod object; `variant` is 'pod' | 'intern-pod'; `tasks` (intern only).
export function PodDetail({ pod, variant = 'pod', tasks = [], onClose }) {
  const { can } = useAuth();
  const [msg, setMsg] = useState(false);
  const isIntern = variant === 'intern-pod';

  const podTasks = isIntern ? tasks.filter(t => t.podId === pod.id) : [];
  const statusData = ['todo', 'in_progress', 'review', 'done'].map(s => ({ status: s.replace('_', ' '), count: podTasks.filter(t => t.status === s).length })).filter(d => d.count > 0);
  const memberProgress = isIntern
    ? (pod.interns || []).map(m => ({ name: m.name.split(' ')[0], progress: avg(podTasks.filter(t => t.assigneeId === m.id).map(t => t.progress)) }))
    : [];

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 700, color: 'var(--txt)' }}>{pod.name}</div>
          <div style={{ fontSize: 11, color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{isIntern ? `Owner: ${pod.ownerName}` : `Lead: ${pod.lead} · SME: ${pod.sme}`}</div>
        </div>
        {can('cwg', 'stakeholder', 'sme') && <button className="btn btn-sm btn-primary" onClick={() => setMsg(true)}>💬 MESSAGE POD</button>}
      </div>

      {isIntern ? (
        <>
          <StatRow items={[
            { l: 'Interns', v: (pod.interns || []).length },
            { l: 'Tasks', v: pod.taskCount },
            { l: 'Done', v: pod.doneCount },
            { l: 'Progress', v: `${pod.progress}%` },
          ]} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {statusData.length > 0 && (
              <ChartCard title="TASKS BY STATUS">
                <PieChart>
                  <Pie data={statusData} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={60} label>
                    {statusData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'Share Tech Mono' }} />
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ChartCard>
            )}
            {memberProgress.length > 0 && (
              <ChartCard title="AVG PROGRESS PER INTERN">
                <BarChart data={memberProgress}>
                  <XAxis dataKey="name" tick={chartAxis} axisLine={false} tickLine={false} />
                  <YAxis tick={chartAxis} axisLine={false} tickLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,212,255,0.05)' }} />
                  <Bar dataKey="progress" fill="#00ff88" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ChartCard>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--txt2)', fontFamily: 'Share Tech Mono', letterSpacing: 1, margin: '6px 0 8px' }}>TASKS</div>
          {podTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--brd2)', fontSize: 12 }}>
              <span style={{ color: 'var(--txt)' }}>{t.title}</span>
              <span style={{ color: 'var(--txt2)', fontFamily: 'Share Tech Mono' }}>{t.assigneeName} · {t.progress}%</span>
            </div>
          ))}
          {podTasks.length === 0 && <div className="empty-state">No tasks in this pod yet</div>}
        </>
      ) : (
        <>
          <StatRow items={[
            { l: 'Status', v: (pod.status || '—').toUpperCase() },
            { l: 'RAG', v: (pod.ragStatus || '—').toUpperCase() },
            { l: 'Members', v: (pod.members || []).length },
            { l: 'Milestone', v: `${pod.currentMilestone || 0}/${(pod.milestones || []).length}` },
          ]} />
          {(pod.milestones || []).length > 0 && (
            <ChartCard title="MILESTONE PROGRESS" height={150}>
              <BarChart data={(pod.milestones || []).map((m, i) => ({ name: `M${i + 1}`, done: i < (pod.currentMilestone || 0) ? 100 : 0 }))}>
                <XAxis dataKey="name" tick={chartAxis} axisLine={false} tickLine={false} />
                <YAxis tick={chartAxis} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(0,212,255,0.05)' }} />
                <Bar dataKey="done" fill="#00d4ff" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ChartCard>
          )}
          <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 12 }}>{pod.description}</div>
          <AttachList title="Members" items={[`${pod.lead} · Lead`, `${pod.sme} · SME`, ...(pod.members || []).map(m => `${m} · Member`)]} />
        </>
      )}

      {msg && <SendMessageModal targetType={isIntern ? 'intern-pod' : 'pod'} targetId={pod.id} targetLabel={pod.name} onClose={() => setMsg(false)} />}
    </Overlay>
  );
}

// ── Quest detail ─────────────────────────────────────────────────────────────
export function QuestDetail({ quest, onClose }) {
  const { can } = useAuth();
  const [msg, setMsg] = useState(false);
  const streamColor = { individual: 'var(--j1)', pod: 'var(--j3)', hybrid: 'var(--j4)' };

  return (
    <Overlay onClose={onClose} width={520}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: 'Orbitron', fontSize: 17, fontWeight: 700, color: 'var(--txt)' }}>{quest.title}</div>
          <span style={{ fontSize: 9, padding: '2px 6px', border: '1px solid', borderRadius: 2, color: streamColor[quest.stream], borderColor: streamColor[quest.stream] + '50', background: streamColor[quest.stream] + '10', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>{quest.stream?.toUpperCase()}</span>
        </div>
        {can('cwg', 'stakeholder', 'sme') && quest.claimedBy && <button className="btn btn-sm btn-primary" onClick={() => setMsg(true)}>💬 MESSAGE CLAIMANT</button>}
      </div>
      <StatRow items={[
        { l: 'Status', v: (quest.status || '—').toUpperCase() },
        { l: 'Bounty', v: `${(quest.bountyMin || 0).toLocaleString()}–${(quest.bountyMax || 0).toLocaleString()}` },
        { l: 'Due', v: quest.deadline ? new Date(quest.deadline).toLocaleDateString() : '—' },
      ]} />
      <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 12 }}>{quest.description}</div>
      <div className="card" style={{ padding: 12, fontSize: 12, color: 'var(--txt2)' }}>
        <div>Claimed by: <span style={{ color: 'var(--txt)' }}>{quest.claimedByName || '— unclaimed —'}</span></div>
        {quest.claimedAt && <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', marginTop: 4 }}>Claimed {new Date(quest.claimedAt).toLocaleString()}</div>}
        <div style={{ marginTop: 6 }}>Tags: {(quest.tags || []).join(', ') || '—'}</div>
      </div>
      {msg && <SendMessageModal targetType="quest" targetId={quest.id} targetLabel={quest.title} onClose={() => setMsg(false)} />}
    </Overlay>
  );
}

function avg(arr) {
  if (!arr || arr.length === 0) return 0;
  return Math.round(arr.reduce((s, n) => s + (n || 0), 0) / arr.length);
}
